// ╔══════════════════════════════════════════════════════════════╗
// ║  Lightweight SVG charts (no external dependency)              ║
// ║   • LineChart       single-series trend                       ║
// ║   • BarChart        single-series                             ║
// ║   • GroupedBarChart double bars (e.g. target vs actual)       ║
// ║   • DonutChart      breakdown by category                     ║
// ╚══════════════════════════════════════════════════════════════╝

import { useId } from 'react';

const fmt = (n) => Number(n ?? 0).toLocaleString('ar-EG');

// ─── LineChart ─────────────────────────────────────────────────
export function LineChart({
  data = [], // [{ label, value }]
  height = 180,
  color = '#cc9c46',
  fillOpacity = 0.18,
  showDots = true,
  yFormatter = fmt,
}) {
  const id = useId();
  if (data.length === 0) return <Empty height={height} />;

  const W = 600;
  const H = height;
  const padL = 40;
  const padR = 16;
  const padT = 16;
  const padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const max = Math.max(...data.map((d) => Number(d.value)), 1);
  const min = Math.min(...data.map((d) => Number(d.value)), 0);
  const range = max - min || 1;

  const xStep = data.length > 1 ? innerW / (data.length - 1) : 0;
  const pts = data.map((d, i) => ({
    x: padL + i * xStep,
    y: padT + innerH - ((Number(d.value) - min) / range) * innerH,
    label: d.label,
    value: d.value,
  }));

  const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const fillD = `${pathD} L ${pts[pts.length - 1].x} ${padT + innerH} L ${pts[0].x} ${padT + innerH} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={fillOpacity * 2} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>

      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((p) => (
        <line
          key={p}
          x1={padL}
          x2={W - padR}
          y1={padT + innerH * (1 - p)}
          y2={padT + innerH * (1 - p)}
          stroke="currentColor"
          strokeOpacity="0.08"
          strokeWidth="1"
        />
      ))}

      {/* Y-axis labels */}
      {[0, 0.5, 1].map((p) => (
        <text
          key={p}
          x={padL - 6}
          y={padT + innerH * (1 - p) + 4}
          fontSize="9"
          textAnchor="end"
          fill="currentColor"
          fillOpacity="0.5"
        >
          {yFormatter(min + range * p)}
        </text>
      ))}

      {/* Fill */}
      <path d={fillD} fill={`url(#${id})`} />

      {/* Line */}
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />

      {/* Dots */}
      {showDots &&
        pts.map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="3" fill={color} />
            <text
              x={p.x}
              y={H - 8}
              fontSize="9"
              textAnchor="middle"
              fill="currentColor"
              fillOpacity="0.6"
            >
              {p.label}
            </text>
          </g>
        ))}
    </svg>
  );
}

// ─── BarChart ──────────────────────────────────────────────────
export function BarChart({
  data = [], // [{ label, value, color? }]
  height = 180,
  color = '#cc9c46',
  yFormatter = fmt,
}) {
  if (data.length === 0) return <Empty height={height} />;

  const W = 600;
  const H = height;
  const padL = 40;
  const padR = 16;
  const padT = 16;
  const padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const max = Math.max(...data.map((d) => Number(d.value)), 1);
  const barW = (innerW / data.length) * 0.6;
  const gap = (innerW / data.length) * 0.4;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none">
      {[0, 0.5, 1].map((p) => (
        <g key={p}>
          <line
            x1={padL}
            x2={W - padR}
            y1={padT + innerH * (1 - p)}
            y2={padT + innerH * (1 - p)}
            stroke="currentColor"
            strokeOpacity="0.08"
            strokeWidth="1"
          />
          <text
            x={padL - 6}
            y={padT + innerH * (1 - p) + 4}
            fontSize="9"
            textAnchor="end"
            fill="currentColor"
            fillOpacity="0.5"
          >
            {yFormatter(max * p)}
          </text>
        </g>
      ))}

      {data.map((d, i) => {
        const v = Math.max(0, Number(d.value));
        const h = (v / max) * innerH;
        const x = padL + i * (barW + gap) + gap / 2;
        const y = padT + innerH - h;
        return (
          <g key={i}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              rx="4"
              fill={d.color || color}
              opacity="0.9"
            />
            <text
              x={x + barW / 2}
              y={H - 8}
              fontSize="9"
              textAnchor="middle"
              fill="currentColor"
              fillOpacity="0.6"
            >
              {d.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── GroupedBarChart (target vs actual) ────────────────────────
export function GroupedBarChart({
  data = [], // [{ label, target, actual }]
  height = 200,
  targetColor = '#a39885',
  actualColor = '#cc9c46',
}) {
  if (data.length === 0) return <Empty height={height} />;

  const W = 700;
  const H = height;
  const padL = 40;
  const padR = 16;
  const padT = 16;
  const padB = 32;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const max = Math.max(
    ...data.flatMap((d) => [Number(d.target), Number(d.actual)]),
    1,
  );
  const groupW = innerW / data.length;
  const barW = (groupW * 0.7) / 2;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none">
      {[0, 0.5, 1].map((p) => (
        <line
          key={p}
          x1={padL}
          x2={W - padR}
          y1={padT + innerH * (1 - p)}
          y2={padT + innerH * (1 - p)}
          stroke="currentColor"
          strokeOpacity="0.08"
        />
      ))}

      {data.map((d, i) => {
        const targetH = (Number(d.target) / max) * innerH;
        const actualH = (Number(d.actual) / max) * innerH;
        const cx = padL + i * groupW + groupW / 2;
        return (
          <g key={i}>
            <rect
              x={cx - barW - 1}
              y={padT + innerH - targetH}
              width={barW}
              height={targetH}
              rx="3"
              fill={targetColor}
              opacity="0.8"
            />
            <rect
              x={cx + 1}
              y={padT + innerH - actualH}
              width={barW}
              height={actualH}
              rx="3"
              fill={actualColor}
            />
            {data.length <= 14 && (
              <text
                x={cx}
                y={H - 10}
                fontSize="8"
                textAnchor="middle"
                fill="currentColor"
                fillOpacity="0.5"
              >
                {d.label}
              </text>
            )}
          </g>
        );
      })}

      {/* Legend */}
      <g transform={`translate(${padL}, ${padT - 4})`}>
        <rect width="10" height="10" fill={targetColor} rx="2" />
        <text x="14" y="9" fontSize="9" fill="currentColor" fillOpacity="0.7">الهدف</text>
        <rect x="60" width="10" height="10" fill={actualColor} rx="2" />
        <text x="74" y="9" fontSize="9" fill="currentColor" fillOpacity="0.7">الفعلي</text>
      </g>
    </svg>
  );
}

// ─── DonutChart ────────────────────────────────────────────────
export function DonutChart({
  data = [], // [{ label, value, color }]
  size = 200,
  thickness = 32,
  centerLabel,
  centerValue,
}) {
  const total = data.reduce((s, d) => s + Number(d.value), 0);
  if (total <= 0) return <Empty height={size} />;

  const R = size / 2 - 4;
  const r = R - thickness;
  const cx = size / 2;
  const cy = size / 2;

  let cumulative = 0;
  const segments = data.map((d) => {
    const v = Number(d.value);
    const start = cumulative / total;
    const end = (cumulative + v) / total;
    cumulative += v;
    return { ...d, start, end, value: v };
  });

  function arc(start, end) {
    const a0 = start * 2 * Math.PI - Math.PI / 2;
    const a1 = end * 2 * Math.PI - Math.PI / 2;
    const x0 = cx + R * Math.cos(a0);
    const y0 = cy + R * Math.sin(a0);
    const x1 = cx + R * Math.cos(a1);
    const y1 = cy + R * Math.sin(a1);
    const x2 = cx + r * Math.cos(a1);
    const y2 = cy + r * Math.sin(a1);
    const x3 = cx + r * Math.cos(a0);
    const y3 = cy + r * Math.sin(a0);
    const large = end - start > 0.5 ? 1 : 0;
    return `M ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1} L ${x2} ${y2} A ${r} ${r} 0 ${large} 0 ${x3} ${y3} Z`;
  }

  return (
    <div className="inline-flex flex-col items-center">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
        {segments.map((s, i) => (
          <path key={i} d={arc(s.start, s.end)} fill={s.color || '#cc9c46'} />
        ))}
        {(centerLabel || centerValue) && (
          <g>
            {centerValue && (
              <text
                x={cx}
                y={cy - 2}
                fontSize="18"
                fontWeight="900"
                textAnchor="middle"
                fill="currentColor"
              >
                {centerValue}
              </text>
            )}
            {centerLabel && (
              <text
                x={cx}
                y={cy + 16}
                fontSize="10"
                textAnchor="middle"
                fill="currentColor"
                fillOpacity="0.6"
              >
                {centerLabel}
              </text>
            )}
          </g>
        )}
      </svg>
      <ul className="mt-3 flex flex-wrap gap-x-3 gap-y-1.5 justify-center max-w-xs">
        {segments.map((s, i) => (
          <li key={i} className="text-[11px] flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ backgroundColor: s.color || '#cc9c46' }}
            />
            <span className="font-semibold">{s.label}</span>
            <span className="text-ink-400 tabular">
              {Math.round((s.value / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Empty({ height = 180 }) {
  return (
    <div
      className="grid place-items-center text-ink-300 text-sm"
      style={{ height }}
    >
      لا توجد بيانات للعرض
    </div>
  );
}
